import Entity from "../../core/entity/Entity";
import { rUniform } from "../../core/util/Random";
import { LevelEntity } from "../LevelEntity";

// Sound parameters for different impact types
const IMPACT_SETTINGS = {
  asteroid: {
    baseFrequency: 800,
    duration: 0.08,
    volume: 0.12,
    noiseGain: 0.6, // Much more noise, less tonal
    filterFreq: 1800,
    waveType: "square" as OscillatorType,
  },
  enemy: {
    baseFrequency: 900,
    duration: 0.06,
    volume: 0.12,
    noiseGain: 0.25,
    filterFreq: 2000,
    waveType: "triangle" as OscillatorType,
  },
  bigBomb: {
    baseFrequency: 600,
    duration: 0.1,
    volume: 0.18,
    noiseGain: 0.4,
    filterFreq: 1500,
    waveType: "sawtooth" as OscillatorType,
  },
};

export default class BulletImpactSoundGenerator
  extends LevelEntity
  implements Entity
{
  private audioContext: AudioContext;

  constructor() {
    super();
    this.tags = ["bulletImpactSoundGenerator"];
    this.id = "bulletImpactSoundGenerator";

    // We'll get the audio context from the game when added
    this.audioContext = null as any;
  }

  onAdd() {
    if (!this.game?.audio) {
      console.warn("No audio context available in game");
      return;
    }

    this.audioContext = this.game.audio;
  }

  /**
   * Handle bullet impact events (non-fatal hits)
   */
  onBulletImpact(eventData: {
    targetType: "asteroid" | "enemy" | "bigBomb";
    position: { x: number; y: number };
    damage: number;
    remainingHealth: number;
  }) {
    if (!this.audioContext || !this.game?.masterGain) {
      return;
    }

    this.synthesizeImpactSound(eventData.targetType, eventData.remainingHealth);
  }

  private synthesizeImpactSound(
    targetType: "asteroid" | "enemy" | "bigBomb",
    remainingHealth: number,
  ) {
    const settings = IMPACT_SETTINGS[targetType] || IMPACT_SETTINGS.enemy;
    const now = this.audioContext.currentTime;

    // Create sharp impact sound components
    this.createImpactClick(now, settings);
    this.createImpactSpark(now, settings, remainingHealth);
  }

  private createImpactClick(now: number, settings: any) {
    // Sharp click for bullet impact
    const oscillator = this.audioContext.createOscillator();
    oscillator.type = settings.waveType;

    // Very brief filter for sharp attack
    const filter = this.audioContext.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.setValueAtTime(settings.filterFreq, now);
    filter.frequency.exponentialRampToValueAtTime(
      settings.filterFreq * 0.3,
      now + settings.duration,
    );
    filter.Q.setValueAtTime(3, now); // High Q for sharp character

    // Sharp attack/decay envelope
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0, now);
    // Reduce oscillator volume for asteroids (more clicky, less tonal)
    const oscillatorVolume =
      settings.noiseGain > 0.5 ? settings.volume * 0.3 : settings.volume;
    gainNode.gain.linearRampToValueAtTime(oscillatorVolume, now + 0.005); // Very fast attack
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + settings.duration); // Quick decay

    // Quick frequency sweep down for impact character
    oscillator.frequency.setValueAtTime(settings.baseFrequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      settings.baseFrequency * 0.6,
      now + settings.duration * 0.5,
    );

    // Connect audio graph
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.game!.masterGain);

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + settings.duration);

    // Cleanup
    oscillator.addEventListener("ended", () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    });
  }

  private createImpactSpark(
    now: number,
    settings: any,
    remainingHealth: number,
  ) {
    // Brief noise burst for impact texture
    const sparkDelay = 0.01; // Start just after the click
    const sparkDuration = settings.duration * 0.6;

    // Create short noise buffer
    const bufferSize = this.audioContext.sampleRate * sparkDuration;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate,
    );
    const data = buffer.getChannelData(0);

    // Generate brief noise burst with sharp decay
    for (let i = 0; i < bufferSize; i++) {
      const progress = i / bufferSize;
      const envelope = Math.pow(1 - progress, 3); // Sharp exponential decay
      data[i] = rUniform(-1, 1) * envelope;
    }

    // Create buffer source
    const noiseSource = this.audioContext.createBufferSource();
    noiseSource.buffer = buffer;

    // High-pass filter for sparkle
    const sparkFilter = this.audioContext.createBiquadFilter();
    sparkFilter.type = "highpass";
    sparkFilter.frequency.setValueAtTime(
      settings.filterFreq * 1.5,
      now + sparkDelay,
    );
    sparkFilter.Q.setValueAtTime(1, now + sparkDelay);

    // Volume based on remaining health (lower health = louder impact)
    const healthFactor = Math.max(0.3, 1.0 - remainingHealth / 10); // Normalize health impact
    const sparkGain = this.audioContext.createGain();
    sparkGain.gain.setValueAtTime(
      settings.noiseGain * healthFactor,
      now + sparkDelay,
    );
    sparkGain.gain.exponentialRampToValueAtTime(
      0.01,
      now + sparkDelay + sparkDuration,
    );

    // Connect and play
    noiseSource.connect(sparkFilter);
    sparkFilter.connect(sparkGain);
    sparkGain.connect(this.game!.masterGain);

    noiseSource.start(now + sparkDelay);
    noiseSource.stop(now + sparkDelay + sparkDuration);

    // Cleanup
    noiseSource.addEventListener("ended", () => {
      noiseSource.disconnect();
      sparkFilter.disconnect();
      sparkGain.disconnect();
    });
  }
}
